import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Package, ArrowRight, Truck } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { api, API } from "@/lib/api";
import { trackPurchase, track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import Thumb from "@/components/visual/Thumb";
import HeardFrom from "@/components/shop/HeardFrom";

export default function OrderConfirmed() {
  const { order_id } = useParams();
  const [search] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    const email = search.get("email");
    const qs = email ? `?email=${encodeURIComponent(email)}` : "";
    api.get(`/orders/${order_id}${qs}`)
      .then((r) => setOrder(r.data))
      .catch(() => setErr("Could not load order"));
  }, [order_id, search]);

  useEffect(() => {
    if (order && order.status === "paid" && !firedRef.current) {
      firedRef.current = true;
      trackPurchase(order);
    }
  }, [order]);

  if (err) return <div className="py-32 text-center font-body text-ink-muted">{err}</div>;
  if (!order) return <div className="py-32 text-center font-body text-ink-muted">Loading your order…</div>;


  return (
    <>
      <Seo title={`Order ${order.order_number}`} description="Thank you for your Karijeeva order." />
      <section className="bg-brand-parchment min-h-screen" data-testid="order-confirmed">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center">
            <div className="inline-flex w-16 h-16 rounded-full bg-brand-obsidian items-center justify-center mb-6">
              <Check className="w-8 h-8 text-brand-gold" strokeWidth={2.5} />
            </div>
            <p className="eyebrow text-brand-gold tracking-[0.3em] mb-3">Order confirmed</p>
            <h1 className="text-h1 text-brand-obsidian leading-tight">Thank you.</h1>
            <p className="font-body text-body-lg text-ink-muted mt-4">Payment received. We pack your order from a lab tested batch and hand it to the courier within 24 to 48 hours. Keep your order number handy to track it.</p>
            <p className="font-display text-h4 text-brand-obsidian mt-6" data-testid="order-number">{order.order_number}</p>
          </div>

          <HeardFrom orderId={order.id} email={search.get("email")} initial={order.heard_from} />

          <div className="mt-14 grid sm:grid-cols-2 gap-4">
            <div className="brand-card p-6 flex gap-4">
              <Truck className="w-6 h-6 text-brand-gold shrink-0" />
              <div>
                <p className="eyebrow tracking-[0.25em] text-brand-gold">Expected delivery</p>
                <p className="font-display text-lg text-brand-obsidian">3 to 7 business days</p>
                <p className="font-body text-xs text-ink-muted mt-1">Metros are usually quicker. <Link to="/shipping-policy" className="underline">Delivery times</Link></p>
              </div>
            </div>
            <div className="brand-card p-6 flex gap-4">
              <Package className="w-6 h-6 text-brand-gold shrink-0" />
              <div>
                <p className="eyebrow tracking-[0.25em] text-brand-gold">Shipping to</p>
                <p className="font-display text-lg text-brand-obsidian leading-tight">{order.address_snapshot?.full_name}</p>
                <p className="font-body text-xs text-ink-muted mt-1">
                  {order.address_snapshot?.city}, {order.address_snapshot?.state} {order.address_snapshot?.pincode}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 brand-card p-8">
            <h3 className="font-display text-xl text-brand-obsidian mb-4">In your order</h3>
            <div className="space-y-3">
              {order.items?.map((i, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="block w-12 h-12 rounded-md overflow-hidden shrink-0"><Thumb src={i.image} alt={i.product_name} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-brand-obsidian">{i.product_name}</p>
                    <p className="font-body text-xs text-ink-muted">{i.variant_size} × {i.quantity}</p>
                  </div>
                  <span className="font-display text-brand-obsidian">₹{i.total_price}</span>
                </div>
              ))}
            </div>
            <div className="h-px bg-brand-gold/20 my-6" />
            <div className="flex items-center justify-between">
              <span className="font-display text-h4 text-brand-obsidian">Total paid</span>
              <span className="font-display text-h4 text-brand-obsidian">₹{order.total?.toFixed?.(2) ?? order.total}</span>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild variant="primary" size="lg">
              <Link to={`/orders/${order.id}/track${search.get("email") ? `?email=${encodeURIComponent(search.get("email"))}` : ""}`} data-testid="order-track-link">Track order <ArrowRight /></Link>
            </Button>
            <Button asChild variant="secondary" size="lg">
              <a href={`${API}/orders/${order.id}/invoice.html${search.get("email") ? `?email=${encodeURIComponent(search.get("email"))}` : ""}`} target="_blank" rel="noreferrer" data-testid="order-invoice-link">View invoice</a>
            </Button>
          </div>
          <div className="mt-12 rounded-3xl bg-brand-obsidian text-white p-8 text-center" data-testid="order-share">
            <p className="font-display text-[24px] tracking-[-0.02em]">Know someone who cooks with coconut oil?</p>
            <p className="mt-2 font-body text-[15px] text-white/75">We are a new, family run brand and grow by word of mouth. Sending us to one person helps more than any ad.</p>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`I just ordered cold pressed coconut oil from Karijeeva. Grown on their own farms in Karnataka and every batch is lab tested: ${window.location.origin}/?utm_source=whatsapp&utm_medium=referral&utm_campaign=post_purchase_share`)}`}
              target="_blank" rel="noreferrer"
              onClick={() => track("share", { method: "whatsapp", content_type: "post_purchase" })}
              className="mt-6 inline-flex items-center rounded-control bg-brand-gold px-6 py-3 font-body text-[15px] text-white"
              data-testid="order-share-whatsapp"
            >
              Share on WhatsApp
            </a>
          </div>
          <p className="mt-6 text-center font-body text-sm text-ink-muted">
            Questions about your order? <Link to="/contact" className="underline">Talk to us</Link> with your order number.
          </p>
        </div>
      </section>
    </>
  );
}
